import { PUSH_NOTIFICATIONS_DEFAULT_STATUS } from './const';
import {
  IPushNotificationsEventListener,
  IPushNotificationsManager,
  IPushNotificationsManagerOptions,
  PushNotificationsStatus,
} from './types';

export class PushNotificationManager implements IPushNotificationsManager {
  #_status: PushNotificationsStatus = PUSH_NOTIFICATIONS_DEFAULT_STATUS;
  #subscription: PushSubscription | null = null;
  #broadcastChannel: BroadcastChannel;

  constructor(private readonly options: IPushNotificationsManagerOptions) {
    this.#broadcastChannel = new BroadcastChannel('pushNotificationMessages');
  }

  async subscribe() {
    const registration = await navigator.serviceWorker?.ready;

    if (!this.#subscription) {
      this.#subscription = await registration.pushManager.getSubscription();
    }

    if (!this.#subscription) {
      try {
        this.#subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.options.applicationServerKey,
        });
      } catch (err) {
        this.#status = PushNotificationsStatus.NotSubscribed;
        throw err;
      }
    }

    try {
      await this.options.onSubscriptionExists?.(this.#subscription.toJSON());
      this.#status = PushNotificationsStatus.Subscribed;
    } catch (err) {
      this.#status = PushNotificationsStatus.NotSubscribed;
      throw err;
    }
  }

  async checkSubscription() {
    let registration: ServiceWorkerRegistration | undefined;

    try {
      registration = await navigator.serviceWorker?.ready;
    } catch (err) {
      console.error(err);
    }

    if (!registration) {
      this.#status = PushNotificationsStatus.NotAvailable;
      return;
    }

    if (!registration.pushManager) {
      this.#status = PushNotificationsStatus.AddingToHomeScreenRequired;
      return;
    }

    if (!this.#subscription) {
      this.#subscription = await registration.pushManager.getSubscription();
    }

    if (!this.#subscription) {
      this.#status = PushNotificationsStatus.NotSubscribed;
      return;
    }

    try {
      await this.options.onSubscriptionExists?.(this.#subscription.toJSON());
      this.#status = PushNotificationsStatus.Subscribed;
    } catch (err) {
      this.#status = PushNotificationsStatus.NotSubscribed;
      throw err;
    }
  }

  set #status(status: PushNotificationsStatus) {
    try {
      if (this.#_status !== status) {
        this.options.onStatusUpdated?.(status);
      }
    } finally {
      this.#_status = status;
    }
  }

  get status(): PushNotificationsStatus {
    return this.#_status;
  }

  get subscription(): PushSubscriptionJSON | undefined {
    return this.#subscription?.toJSON();
  }

  addEventListener(listener: IPushNotificationsEventListener) {
    this.#broadcastChannel.addEventListener('message', listener);
  }

  removeEventListener(listener: IPushNotificationsEventListener) {
    this.#broadcastChannel.removeEventListener('message', listener);
  }
}
